import { canImport } from '../permissions/access';
import { PRICING_V2_VISIBLE } from '../../config/featureFlags';

/**
 * Settings hub: how the existing settings pages are grouped on /settings,
 * plus the words the search box matches against.
 *
 * Keyed by the tab ids in useSettingsTabs so role / reception-mode
 * visibility stays in one place. Nothing here changes the pages themselves:
 * an entry just points at the page as it is today.
 *
 *   description  one line under the name on the hub
 *   keywords     plain words people type ("bank", "no show") that do not
 *                appear in the page name
 *   sections     the section headings on that page today. A search hit on
 *                a section opens the page and scrolls to that heading
 *                (SettingsPageLayout reads location.state.scrollToHeading).
 */

export const HUB_GROUPS = [
    // Self-employed stylists only (the tab is theirs alone).
    { id: 'yours', label: 'Your business', blurb: 'Your choices at the salon', tabIds: ['your-choices'] },
    { id: 'money', label: 'Payments and payouts', blurb: 'Your Beu plan, bank account, card readers', tabIds: ['billing', 'payout-account', 'card-reader'] },
    { id: 'business', label: 'Business and locations', blurb: 'Details, sites, front desk', tabIds: ['salon-details', 'locations', 'business-numbers', 'reception-mode'] },
    { id: 'bookings', label: 'Bookings and diary', blurb: 'Online bookings, sharing, alerts', tabIds: ['booking-url', 'diary-sharing', 'notifications', 'ai-assistant'] },
    // Staging only: the tab is not registered on customer builds, so the group hides itself.
    { id: 'setup', label: 'Setup', blurb: 'Finish setting up, and replay what new salons see', tabIds: ['finish-setup', 'onboarding', 'bailey-welcome', 'diary-demo'] },
    // Staging only, like the demo tools above.
    { id: 'sales', label: 'Sales tools', blurb: 'For exhibitions and demos', tabIds: ['savings-calculator'] },
];

export const HUB_ITEMS = {
    'your-choices': {
        description: 'What the salon can see, and what you join in with',
        keywords: 'choices privacy share sharing my clients private diary visibility who can see marketing salon markets loyalty scheme join booking page show hide prices menu payments get paid self employed',
        sections: ['What', 'Joining in with', 'Your menu and money'],
    },
    'finish-setup': {
        description: 'The jobs worth doing once you are taking bookings',
        keywords: 'finish setup checklist todo to do get started remaining connect payments invite team import clients logo photos deposits cancellation policy',
        sections: ['Finish setting up', 'Bring more in'],
    },
    'savings-calculator': {
        description: 'Show a salon what their software really costs them',
        keywords: 'savings calculator exhibition stand show event demo sales close pitch cost compare fresha treatwell booksy phorest timely commission admin leads sign up qr',
        sections: ['Savings calculator', 'Details left at the stand'],
    },
    'diary-demo': {
        description: 'Fill the diary with realistic bookings for a demo',
        keywords: 'demo dummy fake sample bookings diary screenshot mockup marketing sales pitch populate fill colours palette theme barber salon beauty',
        sections: ['Diary demo', 'What the demo changes'],
    },
    'bailey-welcome': {
        description: 'Play the welcome and diary tour new salons see first',
        keywords: 'bailey welcome intro hero tour palette sidekick first run replay new salon',
        sections: [],
    },
    'onboarding': {
        description: 'Restart or replay the setup flow new salons see',
        keywords: 'onboarding setup demo restart replay first login import checklist reset walkthrough',
        sections: ['Where you are', 'Replay', 'Demo files'],
    },
    'salon-details': {
        description: 'Name, address, photos, opening hours',
        keywords: 'salon business name address logo photos images branding email colour hours open close delete',
        sections: ['Business Details', 'Business Images', 'Email Branding', 'Opening Hours', 'Delete Salon'],
    },
    'locations': {
        description: 'Your locations, team access and shared settings',
        keywords: 'sites branches multi location second salon team access add a location group copy settings franchise booking links',
        sections: ['Your locations', 'Team access', 'Shared settings', 'Copy settings', 'Add a location'],
    },
    'business-numbers': {
        description: 'Phone numbers for calls and two-way texts',
        keywords: 'phone number numbers mobile landline texts sms two way reply bailey calls receptionist divert forward',
        sections: ['Business numbers'],
    },
    'reception-mode': {
        description: 'Lock a shared front-desk device',
        keywords: 'reception mode lock shared front desk device pin hide confidential visibility staff see earnings bailey',
        sections: ['Reception', 'What the front desk can see and do', 'PIN'],
    },
    'booking-url': {
        description: 'Online bookings and no-show protection',
        keywords: 'booking link url page website widget instagram window how far ahead reschedule no show no-show fee cancel cancellation policy deposit charge card guest guests account verify code otp marketplace',
        sections: ['Booking Link', 'Your booking page', 'Guest Bookings', 'Booking Window', 'Reschedule', 'No Show and Cancellation', 'No Show Fee'],
    },
    'diary-sharing': {
        description: 'Share your diary and set team visibility',
        keywords: 'diary sharing share link calendar visibility team see',
        sections: ['Your diary', 'Team diary visibility'],
    },
    'notifications': {
        description: 'What reaches you in Beu and by email',
        keywords: 'notifications alerts push staff activity new booking reminders',
        sections: ['Notifications', 'Staff activity', 'Share with your salon'],
    },
    'card-reader': {
        description: 'Card readers, payment methods, fees',
        keywords: 'card reader terminal tap pair machine contactless cash payment methods transaction fees stripe tips',
        sections: ['Card Reader', 'Connected Card Readers', 'Pair New Reader', 'Payment Methods', 'Transaction Fees', 'Card Reader Tips'],
    },
    'payout-account': {
        description: 'Bank account, payout funds, history',
        keywords: 'bank account payout get paid withdraw money transfer sort code balance funds when do i get paid history statement earnings stripe connected approved verified status review kyc identity documents',
        sections: ['Payouts', 'Add Bank Account', 'Payout Funds', 'Withdrawal History', 'Fee Details'],
    },
    'billing': PRICING_V2_VISIBLE ? {
        description: 'Your plan, usage, message balance, cards and invoices',
        keywords: 'subscription plan pricing price usage invoice receipt billing upgrade beu + bailey cancel charge card on file credits balance top up auto top-up texts emails reminders allowance bailey questions receptionist minutes replies packs phone numbers settle negative per month',
        sections: ['Your plan', 'This month', 'Message balance', 'Payment', 'How pricing works'],
    } : {
        description: 'Your Beu subscription and invoices',
        keywords: 'subscription plan invoice receipt billing upgrade cancel beu charge card on file sms credits top up purchases price per month',
        sections: ['Payment Methods', 'Invoices', 'Top-ups'],
    },
    'ai-assistant': {
        description: 'Bailey answers clients and books them in, day and night',
        keywords: 'ai assistant receptionist bailey concierge chatbot phone voice auto reply messages front desk soul call back',
        sections: ['Front desk', 'Soul', 'Phone'],
    },
};

/**
 * Pages reachable from settings that are not tabs in the strip (they live in
 * the sidenav profile menu). Searchable by every role.
 */
export const HUB_EXTRA_ITEMS = [
    {
        id: 'import-data', label: 'Import your data', path: '/settings/import-data',
        // Owner, `settings`, or a self-employed stylist (importFacade's rule).
        allowed: () => canImport(),
        description: 'Bring in clients, services, products and your team from a file',
        keywords: 'import upload csv excel spreadsheet pdf photo migrate move from fresha treatwell phorest booksy square vagaro timely bring in data clients list price list',
        sections: [],
    },
    {
        id: 'edit-password', label: 'Change Password', path: '/settings/edit-password',
        description: 'Keep your account secure',
        keywords: 'password change reset security login sign in account',
        sections: [],
    },
];

/**
 * Search the visible tabs. Returns up to `limit` rows of
 * { tab, section | null, score }. A hit on a section heading ("no show")
 * ranks above a hit on a page's keyword list, and a page-level row is
 * kept so the page itself is always reachable.
 */
export function searchSettings(query, tabs, limit = 7) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return [];
    const words = q.split(/\s+/);
    const out = [];

    [...tabs, ...HUB_EXTRA_ITEMS.filter((x) => !x.allowed || x.allowed())].forEach((tab) => {
        const meta = HUB_ITEMS[tab.id] || tab;
        const group = HUB_GROUPS.find(g => g.tabIds.includes(tab.id));
        const hay = [tab.label, meta.description, meta.keywords, group && group.label].filter(Boolean).join(' ').toLowerCase();
        if (words.every(w => hay.includes(w))) {
            const label = tab.label.toLowerCase();
            const score = label.includes(q) ? 3 : (meta.description || '').toLowerCase().includes(q) ? 2 : 1;
            out.push({ tab, section: null, score });
        }
        (meta.sections || []).forEach((section) => {
            const sh = section.toLowerCase();
            if (words.every(w => sh.includes(w) || hay.includes(w)) && words.some(w => sh.includes(w))) {
                out.push({ tab, section, score: sh.includes(q) ? 4 : 2.5 });
            }
        });
    });

    out.sort((a, b) => b.score - a.score);
    const seen = new Set();
    return out.filter((r) => {
        const key = `${r.tab.id}|${r.section || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    }).slice(0, limit);
}
